import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { filterTemplateSnapshot } from '@/services/formTemplateService';
import { bulkGetFormData } from '@/services/formDataService';
import { useAuthStore } from '@/stores/authStore';
import type { OnboardingForm } from './types';

/**
 * Fetches default template snapshot + bulk form data,
 * then derives which forms are pending (no form data saved yet).
 */
export function useOnboardingForms() {
  const session = useAuthStore((s) => s.session);
  const objectType = session?.organization.type ?? '';
  const objectId = session?.tenant.id ?? '';

  // 1. Fetch template snapshot filtered by isDefault=true
  const snapshotQuery = useQuery({
    queryKey: ['templateSnapshot', 'filter', objectType, objectId, { isDefault: true }],
    queryFn: () => filterTemplateSnapshot(objectType, objectId, { attributes: { isDefault: true } }),
    enabled: !!objectType && !!objectId,
  });

  // Extract all formConfigIds from the snapshot for the bulk fetch
  const allFormConfigIds = useMemo(() => {
    if (!snapshotQuery.data?.tabs) return [];
    return snapshotQuery.data.tabs.flatMap((tab) =>
      tab.forms
        .filter((f) => f.accessLevel === 'READ_WRITE')
        .map((f) => f.formConfigId),
    );
  }, [snapshotQuery.data]);

  // Stable string key to avoid infinite re-renders from array reference changes
  const bulkKeyString = allFormConfigIds.slice().sort().join(',');

  // 2. Bulk fetch form data for all default forms
  const bulkQuery = useQuery({
    queryKey: ['formData', 'bulk', bulkKeyString],
    queryFn: () =>
      bulkGetFormData(objectType, objectId, { formConfigIds: allFormConfigIds }),
    enabled: allFormConfigIds.length > 0 && !!objectType && !!objectId,
    retry: 1,
  });

  // 3. Derive onboarding forms with pending status
  const { forms, pendingForms } = useMemo(() => {
    if (!snapshotQuery.data?.tabs) return { forms: [], pendingForms: [] };

    const savedFormConfigIds = new Set(
      (bulkQuery.data ?? []).map((d) => d.formConfigId),
    );

    const allForms: OnboardingForm[] = snapshotQuery.data.tabs.flatMap((tab) =>
      tab.forms
        .filter((f) => f.accessLevel === 'READ_WRITE')
        .map((f) => ({
          formConfigId: f.formConfigId,
          formName: f.formName ?? 'Untitled Form',
          accessLevel: f.accessLevel,
          tabName: tab.name,
          status: savedFormConfigIds.has(f.formConfigId)
            ? ('saved' as const)
            : ('pending' as const),
        })),
    );

    return {
      forms: allForms,
      pendingForms: allForms.filter((f) => f.status === 'pending'),
    };
  }, [snapshotQuery.data, bulkQuery.data]);

  const isLoading = snapshotQuery.isLoading || (allFormConfigIds.length > 0 && bulkQuery.isLoading);
  const isError = snapshotQuery.isError || bulkQuery.isError;

  return {
    forms,
    pendingForms,
    hasPendingForms: pendingForms.length > 0,
    isLoading,
    isError,
    refetch: () => {
      void snapshotQuery.refetch();
      void bulkQuery.refetch();
    },
  };
}
